import { useState, useEffect, useRef, useCallback } from 'react';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { fetchAlertasQuedaVendas, fetchProdutoDetalhe } from '../../services/api';
import AlertaHoverCard from './AlertaHoverCard';

/**
 * Alertas de Vendas — modal (estilo Compras Automáticas), ESCOPADO ao catálogo:
 *   escopo = 'ecommerce' | 'centro' | 'caxias'
 * Lista os itens que quebraram o padrão de venda (caíram >=70% por >=4 dias) e
 * ainda têm estoque. Passe o mouse num item pra ver o detalhamento do mês atual.
 */
const ESCOPOS = {
  ecommerce: { label: 'E-commerce', tipo: 'ecommerce', loja: '' },
  centro: { label: 'Loja Centro', tipo: 'loja-fisica', loja: '001' },
  caxias: { label: 'Loja Caxias', tipo: 'loja-fisica', loja: '004' },
};

export default function AlertasVendasModal({ isOpen, onClose, escopo = 'ecommerce' }) {
  const cfg = ESCOPOS[escopo] || ESCOPOS.ecommerce;
  const [itens, setItens] = useState([]);
  const [params, setParams] = useState(null);
  const [gap, setGap] = useState(false);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState(null);

  // hover / detalhe travado (pin)
  const [hoverItem, setHoverItem] = useState(null);
  const [pinnedItem, setPinnedItem] = useState(null); // clique trava o card pra rolar
  const [hoverDetalhe, setHoverDetalhe] = useState(null);
  const [hoverLoading, setHoverLoading] = useState(false);
  const [hoverPos, setHoverPos] = useState({ top: 0, left: 0 });
  const detalheCache = useRef({});
  const hoverReqRef = useRef(0);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    try {
      const resp = await fetchAlertasQuedaVendas(escopo);
      if (resp && resp.success) {
        setItens(resp.itens || []);
        setParams(resp.parametros || null);
        setGap(!!resp.gap);
      } else {
        setErro('Não consegui carregar os alertas agora. Clique em "Recarregar".');
      }
    } catch (_) {
      setErro('Não consegui carregar os alertas agora. Clique em "Recarregar".');
    } finally {
      setCarregando(false);
    }
  }, [escopo]);

  useEffect(() => {
    if (isOpen) {
      setHoverItem(null);
      setPinnedItem(null);
      carregar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Carrega o detalhe do produto (cache-aware) pra alimentar o card
  const carregarDetalhe = useCallback((item) => {
    const cached = detalheCache.current[item.codigo];
    if (cached !== undefined) {
      setHoverDetalhe(cached);
      setHoverLoading(false);
      return;
    }
    const reqId = ++hoverReqRef.current;
    setHoverDetalhe(null);
    setHoverLoading(true);
    fetchProdutoDetalhe(cfg.tipo, item.codigo, cfg.loja)
      .then((d) => {
        const detalhe = (d && (d.data || d)) || null;
        detalheCache.current[item.codigo] = detalhe;
        if (reqId === hoverReqRef.current) { setHoverDetalhe(detalhe); setHoverLoading(false); }
      })
      .catch(() => {
        detalheCache.current[item.codigo] = null;
        if (reqId === hoverReqRef.current) { setHoverDetalhe(null); setHoverLoading(false); }
      });
  }, [cfg.tipo, cfg.loja]);

  const onRowHover = useCallback((item, e) => {
    if (pinnedItem) return; // travado: não segue o mouse
    setHoverItem(item);
    // posiciona o card na calha à esquerda do modal (não cobre os números à direita)
    const W = 320;
    const modalW = Math.min(1000, window.innerWidth * 0.95);
    const gutter = (window.innerWidth - modalW) / 2;
    const left = gutter >= W + 18 ? (gutter - W - 14) : 12;
    let top = (e.clientY || 0) - 50;
    const Hv = window.innerHeight * 0.82;
    if (top + Hv > window.innerHeight - 12) top = Math.max(12, window.innerHeight - Hv - 12);
    if (top < 12) top = 12;
    setHoverPos({ top, left });
    carregarDetalhe(item);
  }, [pinnedItem, carregarDetalhe]);

  // Clique trava o card (modal centralizado e rolável)
  const onRowClick = useCallback((item) => {
    setHoverItem(null);
    setPinnedItem(item);
    carregarDetalhe(item);
  }, [carregarDetalhe]);

  if (!isOpen) return null;

  const exportarCSV = () => {
    const head = ['Codigo', 'Produto', 'Queda_pct', 'Vd_media_dia', 'Vd_atual_dia', 'Estoque'];
    const linhas = itens.map(i => [
      i.codigo, `"${String(i.nome || '').replace(/"/g, '""')}"`,
      i.quedaPct, i.baselineDiario, i.atualDiario, `"${String(i.estoqueInfo || '').replace(/"/g, '""')}"`
    ].join(','));
    const csv = [head.join(','), ...linhas].join('\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `alertas-vendas-${escopo}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  const gerarPDF = () => {
    const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
    const dataStr = new Date().toLocaleDateString('pt-BR');
    doc.setFontSize(16); doc.setTextColor(40);
    doc.text(`Alertas de Vendas — ${cfg.label}`, 40, 40);
    doc.setFontSize(10); doc.setTextColor(120);
    const regra = params ? `Queda >=${params.limiarQuedaPct}% por >=${params.diasQueda} dias, com estoque` : 'Quebra no padrão de venda';
    doc.text(`Gerado em ${dataStr}  ·  ${regra}  ·  ${itens.length} item(ns)`, 40, 58);
    const body = itens.map(i => [
      i.codigo, i.nome || '', `${i.quedaPct}%`, String(i.baselineDiario), String(i.atualDiario), i.estoqueInfo || ''
    ]);
    autoTable(doc, {
      startY: 74,
      head: [['Código', 'Produto', 'Queda', 'Vd média/dia', 'Vd atual/dia', 'Estoque']],
      body,
      styles: { fontSize: 8, cellPadding: 4, overflow: 'linebreak' },
      headStyles: { fillColor: [220, 38, 38], textColor: 255, fontStyle: 'bold' },
      columnStyles: { 1: { cellWidth: 180 }, 2: { fontStyle: 'bold', halign: 'center' } },
    });
    doc.save(`alertas-vendas-${escopo}.pdf`);
  };

  const th = { padding: '9px 10px', fontSize: '0.7rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.3px', textAlign: 'left', position: 'sticky', top: 0, background: '#f8fafc', borderBottom: '1px solid #e2e8f0', zIndex: 1 };
  const td = { padding: '8px 10px', fontSize: '0.85rem', color: '#1e293b', borderBottom: '1px solid #f1f5f9', verticalAlign: 'middle' };

  return (
    <>
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 1050 }} onClick={onClose} />
      <div
        role="dialog"
        aria-modal="true"
        style={{
          position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
          width: 'min(1000px, 95vw)', maxHeight: '90vh', background: '#fff', borderRadius: 16,
          boxShadow: '0 24px 64px rgba(15,23,42,0.32)', zIndex: 1051, display: 'flex', flexDirection: 'column', overflow: 'hidden'
        }}
      >
        {/* Cabeçalho */}
        <div style={{ background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)', color: '#fff', padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 14 }}>
          <span style={{ fontSize: '1.6rem', lineHeight: 1 }}>⚠️</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 800, fontSize: '1.12rem' }}>Alertas de Vendas · {cfg.label}</div>
            <div style={{ fontSize: '0.8rem', opacity: 0.92 }}>
              {params
                ? `Itens que caíram ≥${params.limiarQuedaPct}% por ≥${params.diasQueda} dias seguidos e ainda têm estoque · passe o mouse pra ver o mês atual`
                : 'Itens que quebraram o padrão de venda e ainda têm estoque'}
            </div>
          </div>
          <button onClick={onClose} title="Fechar" style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', width: 32, height: 32, borderRadius: 8, fontSize: '1.1rem', cursor: 'pointer' }}>✕</button>
        </div>

        {/* Resumo */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', padding: '12px 20px', borderBottom: '1px solid #eef2f7', background: '#fcfcfd' }}>
          <span style={{ fontSize: '0.84rem', color: '#475569', fontWeight: 700 }}>{cfg.label}</span>
          <div style={{ flex: 1 }} />
          {!carregando && !erro && (
            <span style={{ background: '#fef2f2', color: '#b91c1c', padding: '4px 10px', borderRadius: 999, fontSize: '0.78rem', fontWeight: 700 }}>{itens.length} alerta(s)</span>
          )}
        </div>

        {/* Corpo */}
        <div style={{ overflowY: 'auto', flex: 1 }}>
          {carregando ? (
            <div style={{ padding: '48px 20px', textAlign: 'center', color: '#64748b' }}>
              <div style={{ fontSize: '1.8rem', marginBottom: 10 }}>⏳</div>
              <div style={{ fontWeight: 600 }}>Analisando o padrão de vendas…</div>
            </div>
          ) : erro ? (
            <div style={{ padding: '40px 20px', textAlign: 'center', color: '#b91c1c' }}>
              <div style={{ marginBottom: 12 }}>{erro}</div>
              <button onClick={carregar} style={botaoSec}>Recarregar</button>
            </div>
          ) : gap ? (
            <div style={{ padding: '40px 20px', textAlign: 'center', color: '#b45309' }}>
              <div style={{ fontSize: '1.8rem', marginBottom: 8 }}>⏳</div>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>Dados de vendas desta loja parecem desatualizados</div>
              <div style={{ fontSize: '0.85rem', color: '#94a3b8' }}>Os alertas ficam suspensos até a sincronização das vendas recentes voltar ao normal (evita alarme falso).</div>
            </div>
          ) : itens.length === 0 ? (
            <div style={{ padding: '48px 20px', textAlign: 'center', color: '#16a34a' }}>
              <div style={{ fontSize: '1.8rem', marginBottom: 8 }}>✅</div>
              Nenhum item quebrou o padrão de venda neste catálogo. Tudo certo!
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={th}>Produto</th>
                  <th style={{ ...th, textAlign: 'center', width: 100 }}>Vd média/dia</th>
                  <th style={{ ...th, textAlign: 'center', width: 100 }}>Vd atual/dia</th>
                  <th style={{ ...th, textAlign: 'center', width: 80 }}>Queda</th>
                  <th style={{ ...th, textAlign: 'right', width: 130 }}>Estoque</th>
                </tr>
              </thead>
              <tbody>
                {itens.map(i => (
                  <tr
                    key={`${i.origem}-${i.codigo}`}
                    onMouseEnter={(e) => onRowHover(i, e)}
                    onMouseMove={(e) => onRowHover(i, e)}
                    onMouseLeave={() => !pinnedItem && setHoverItem(null)}
                    onClick={() => onRowClick(i)}
                    style={{ background: '#fff', cursor: 'pointer' }}
                  >
                    <td style={td}>
                      <div style={{ fontWeight: 600, lineHeight: 1.25, maxWidth: 360, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title="Clique pra travar o detalhamento e rolar as datas">{i.nome}</div>
                      <div style={{ fontSize: '0.72rem', color: '#94a3b8' }}>#{i.codigo}</div>
                    </td>
                    <td style={{ ...td, textAlign: 'center', fontWeight: 700, color: '#475569' }}>{i.baselineDiario}</td>
                    <td style={{ ...td, textAlign: 'center', fontWeight: 700, color: '#0f766e' }}>{i.atualDiario}</td>
                    <td style={{ ...td, textAlign: 'center' }}>
                      <span style={{ fontWeight: 800, color: '#b91c1c', background: '#fee2e2', padding: '2px 8px', borderRadius: 999, fontSize: '0.8rem' }}>▼ {i.quedaPct}%</span>
                    </td>
                    <td style={{ ...td, textAlign: 'right', color: '#334155', fontWeight: 600 }}>{i.estoqueInfo}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Rodapé */}
        <div style={{ borderTop: '1px solid #eef2f7', padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 10, background: '#fcfcfd', flexWrap: 'wrap' }}>
          <div style={{ fontSize: '0.78rem', color: '#94a3b8', flex: 1, minWidth: 180 }}>
            {itens.length > 0 ? 'Itens com estoque que pararam de vender — vale investigar preço, vitrine ou anúncio.' : ''}
          </div>
          <button onClick={carregar} style={botaoSec} title="Recalcular agora">↻ Recarregar</button>
          <button onClick={gerarPDF} disabled={!itens.length} style={{ ...botaoSec, opacity: itens.length ? 1 : 0.5 }}>📑 Gerar PDF</button>
          <button onClick={exportarCSV} disabled={!itens.length} style={{ ...botaoSec, opacity: itens.length ? 1 : 0.5 }}>📄 CSV</button>
        </div>
      </div>

      {/* Card de hover — espiada rápida (segue o mouse) */}
      {hoverItem && !pinnedItem && (
        <AlertaHoverCard
          item={hoverItem}
          detalhe={hoverDetalhe}
          loading={hoverLoading}
          style={{ top: hoverPos.top, left: hoverPos.left, zIndex: 1000000 }}
        />
      )}

      {/* Card travado — clique no produto: centralizado e rolável */}
      {pinnedItem && (
        <AlertaHoverCard
          item={pinnedItem}
          detalhe={hoverDetalhe}
          loading={hoverLoading}
          pinned
          onClose={() => setPinnedItem(null)}
        />
      )}
    </>
  );
}

const botaoSec = { background: '#fff', border: '1px solid #cbd5e1', color: '#475569', borderRadius: 10, padding: '9px 14px', fontWeight: 700, fontSize: '0.84rem', cursor: 'pointer' };
